import React from 'react';
import { Button, Result, Space, Typography } from 'antd';
import { reportClientError, type ClientErrorReport } from '../observability/report';

type ErrorBoundaryProps = {
  /** 用于 route 切换时 reset 边界(由父层把 location.key 等作为 resetKey 传入) */
  resetKey?: string | number;
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  report: ClientErrorReport | null;
};

/**
 * 全应用 React 错误边界(HARDENING_ROADMAP 批1 / P0-d)。
 *
 * 在此之前 Admin 全仓无 ErrorBoundary / componentDidCatch, 任一渲染异常会整页白屏。
 * 本组件把渲染异常降级为可操作卡片(重新加载 / 返回首页), 并展示后端 request_id 便于排查,
 * 同时通过统一可观测性 hook 上报。
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, report: null };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const report = reportClientError({
      source: 'react.error-boundary',
      error,
      componentStack: info.componentStack ?? null,
    });
    this.setState({ report });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // route 切换后自动恢复, 避免一个页面崩了导致其它页面也卡在降级态。
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  private reset = (): void => {
    this.setState({ hasError: false, report: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleBackHome = (): void => {
    // 用原生跳转而非 router, 确保即使 router 子树异常也能回到安全首页并重建。
    const base = window.location.pathname.startsWith('/admin') ? '/admin/' : '/';
    window.location.assign(base);
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { report } = this.state;
    return (
      <div className="error-boundary-fallback">
        <Result
          status="error"
          title="页面出错了"
          subTitle="页面渲染时发生异常。你可以重新加载本页，或返回首页继续操作。"
          extra={
            <Space>
              <Button type="primary" onClick={this.handleReload}>
                重新加载
              </Button>
              <Button onClick={this.handleBackHome}>返回首页</Button>
            </Space>
          }
        >
          {report?.requestId || report?.message ? (
            <div className="error-boundary-detail">
              {report?.message ? (
                <Typography.Paragraph type="secondary" className="error-boundary-message">
                  {report.message}
                </Typography.Paragraph>
              ) : null}
              {report?.requestId ? (
                <Typography.Text type="secondary" copyable={{ text: report.requestId }}>
                  错误编号 request_id：{report.requestId}
                </Typography.Text>
              ) : null}
            </div>
          ) : null}
        </Result>
      </div>
    );
  }
}

export default ErrorBoundary;
